import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, imageUrl, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";
import { Trash2, Plus, Upload, ImageOff, Loader2, FolderTree, ChevronRight } from "lucide-react";

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const NONE = "__none__"; // Radix Select requires a non-empty value — sentinel for "no parent"

const emptyForm = { name: "", slug: "", image_url: "", display_order: 0, parent_id: NONE, active: true };

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/categories").then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);

  const tops = useMemo(() => cats.filter((c) => !c.parent_id), [cats]);
  const childrenOf = useMemo(() => {
    const map = {};
    for (const c of cats) {
      if (c.parent_id) (map[c.parent_id] ||= []).push(c);
    }
    return map;
  }, [cats]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = (parentId = NONE) => {
    setEditing(null);
    setForm({ ...emptyForm, parent_id: parentId, display_order: cats.length + 1 });
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c.id);
    setForm({ ...emptyForm, ...c, parent_id: c.parent_id || NONE });
    setOpen(true);
  };

  const closeDialog = (v) => {
    setOpen(v);
    if (!v) { setEditing(null); setForm(emptyForm); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      set("image_url", data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        slug: form.slug || slugify(form.name),
        display_order: +form.display_order,
        parent_id: form.parent_id === NONE ? null : form.parent_id,
      };
      if (editing) await api.put(`/admin/categories/${editing}`, payload);
      else await api.post("/admin/categories", payload);
      toast.success(editing ? "Category updated" : "Category created");
      closeDialog(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    await api.delete(`/admin/categories/${c.id}`);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Categories</h1>
          <p className="text-xs text-hyd-text2 mt-1">Top-level categories (Al a carte, Fancy Crackers, Gift Boxes) and their sub-categories.</p>
        </div>
        <Button
          data-testid="admin-category-add-btn"
          onClick={() => openNew(NONE)}
          className="bg-hyd-gold text-hyd-bg hover:bg-hyd-goldDim font-bold rounded-full"
        >
          <Plus className="w-4 h-4 mr-1" /> Add category
        </Button>
      </div>

      <div className="mt-5 space-y-3">
        {tops.map((t) => (
          <section key={t.id} className="bg-hyd-panel border border-hyd-border rounded-2xl overflow-hidden">
            <header className="flex items-center gap-3 px-4 py-3 bg-hyd-panel2 border-b border-hyd-border">
              <div className="w-10 h-10 rounded-xl bg-hyd-bg overflow-hidden grid place-items-center">
                {t.image_url ? <img src={imageUrl(t.image_url)} alt={t.name} className="w-full h-full object-cover" /> : <FolderTree className="w-4 h-4 text-hyd-gold" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg font-bold text-hyd-text">{t.name}</div>
                <div className="text-[11px] text-hyd-text3">{(childrenOf[t.id] || []).length} sub-categories · slug: {t.slug}</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => openEdit(t)} className="text-hyd-gold">Edit</Button>
              <Button size="sm" variant="ghost" onClick={() => openNew(t.id)} data-testid={`admin-subcategory-add-${t.slug}`} className="text-hyd-text2 hover:text-hyd-gold">
                <Plus className="w-3.5 h-3.5 mr-1" /> Sub-category
              </Button>
              <Button size="sm" variant="ghost" onClick={() => del(t)} className="text-hyd-red"><Trash2 className="w-3.5 h-3.5" /></Button>
            </header>

            <table className="w-full text-sm">
              <thead className="text-hyd-text2 text-[10px] uppercase tracking-widest">
                <tr>
                  <th className="text-left pl-10 py-2">Sub-category</th>
                  <th className="text-left p-2">Slug</th>
                  <th className="text-right p-2">Order</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-hyd-border/60">
                {(childrenOf[t.id] || []).length === 0 && (
                  <tr><td colSpan="4" className="pl-10 py-3 text-hyd-text3 text-xs">No sub-categories yet.</td></tr>
                )}
                {(childrenOf[t.id] || []).map((c) => (
                  <tr key={c.id} className="hover:bg-hyd-panel2/60">
                    <td className="pl-10 py-2">
                      <div className="flex items-center gap-2">
                        <ChevronRight className="w-3 h-3 text-hyd-text3" />
                        <div className="w-7 h-7 rounded-full bg-hyd-panel2 overflow-hidden grid place-items-center">
                          {c.image_url ? <img src={imageUrl(c.image_url)} alt={c.name} className="w-full h-full object-cover" /> : <ImageOff className="w-3 h-3 text-hyd-text3" />}
                        </div>
                        <span>{c.name}</span>
                      </div>
                    </td>
                    <td className="p-2 text-hyd-text2">{c.slug}</td>
                    <td className="p-2 text-right">{c.display_order}</td>
                    <td className="p-2 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`category-edit-${c.slug}`} className="text-hyd-gold">Edit</Button>
                      <Button size="sm" variant="ghost" onClick={() => del(c)} className="text-hyd-red"><Trash2 className="w-3 h-3" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ))}
        {tops.length === 0 && (
          <div className="bg-hyd-panel border border-hyd-border rounded-2xl p-6 text-center text-hyd-text2 text-sm">
            No top-level categories yet. Click "Add category" to create one.
          </div>
        )}
      </div>

      {/* Add / Edit popup */}
      <Dialog open={open} onOpenChange={closeDialog}>
        <DialogContent className="bg-hyd-panel border-hyd-border text-hyd-text max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editing ? "Edit category" : (form.parent_id && form.parent_id !== NONE ? "New sub-category" : "New category")}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-3" data-testid="category-dialog-form">
            <F label="Name">
              <Input data-testid="category-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} onBlur={() => !form.slug && set("slug", slugify(form.name))} required />
            </F>
            <F label="Slug">
              <Input value={form.slug} onChange={(e) => set("slug", slugify(e.target.value))} placeholder="auto" />
            </F>

            <F label="Parent category">
              <Select value={form.parent_id || NONE} onValueChange={(v) => set("parent_id", v)}>
                <SelectTrigger data-testid="category-parent-select" className="bg-hyd-bg border-hyd-border">
                  <SelectValue placeholder="Top-level (no parent)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Top-level (no parent)</SelectItem>
                  {tops.filter((t) => t.id !== editing).map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </F>

            <div>
              <Label className="text-xs text-hyd-text2 mb-1 block">Category image</Label>
              {form.image_url ? (
                <div className="relative w-full aspect-[3/2] rounded-2xl overflow-hidden border border-hyd-border bg-hyd-bg">
                  <img src={imageUrl(form.image_url)} alt="Category preview" data-testid="category-image-preview" className="w-full h-full object-cover" />
                  <div className="absolute top-2 right-2 flex gap-1.5">
                    <button type="button" onClick={() => fileRef.current?.click()} title="Upload new image" data-testid="category-image-replace-btn" className="h-8 w-8 grid place-items-center rounded-full bg-hyd-bg/85 text-hyd-gold hover:text-hyd-goldDim">
                      <Upload className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={() => set("image_url", "")} title="Delete image" data-testid="category-image-delete-btn" className="h-8 w-8 grid place-items-center rounded-full bg-hyd-bg/85 text-hyd-red hover:bg-hyd-red hover:text-white">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <button type="button" onClick={() => fileRef.current?.click()} data-testid="category-image-upload-btn" className="w-full aspect-[3/2] rounded-2xl border-2 border-dashed border-hyd-border grid place-items-center text-xs text-hyd-text2 hover:border-hyd-gold hover:text-hyd-gold transition-colors">
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin text-hyd-gold" /> : (
                    <div className="text-center"><Upload className="w-5 h-5 mx-auto" /><div className="mt-1">Upload image</div></div>
                  )}
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" onChange={uploadImage} className="hidden" />
            </div>

            <F label="Display order">
              <Input type="number" value={form.display_order} onChange={(e) => set("display_order", e.target.value)} />
            </F>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.active} onCheckedChange={(v) => set("active", !!v)} /> Active
            </label>

            <div className="flex gap-2 pt-1">
              <Button data-testid={ADMIN.categorySave} type="submit" className="flex-1 bg-hyd-gold text-hyd-bg hover:bg-hyd-goldDim font-bold rounded-full">
                {editing ? "Save changes" : (<><Plus className="w-4 h-4 mr-1" />Create</>)}
              </Button>
              <Button type="button" variant="ghost" onClick={() => closeDialog(false)} className="text-hyd-text2">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-hyd-text2 mb-1 block">{label}</Label>
      <div className="[&_input]:bg-hyd-bg [&_input]:border-hyd-border [&_input]:text-hyd-text">{children}</div>
    </div>
  );
}
